/**
 * QR destination validation + normalisation — client mirror of the backend's
 * App\Services\QrLinks\QrTargetNormalizer (the server is authoritative; this
 * gives the owner instant feedback and the same canonical URL). https only.
 */

const MAX_URL = 2048;
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;

/** `code` is an i18n suffix: promos.v2_qre_<code>. */
export class QrTargetError extends Error {
  constructor(code) { super(code); this.code = code; }
}

const fail = (code) => { throw new QrTargetError(code); };

/** Any value that carries an explicit non-https scheme (javascript:, data:, http:, ftp:, //) is rejected outright. */
function assertNoBadScheme(raw) {
  const s = raw.trim();
  if (s.startsWith('//')) fail('scheme');
  const m = s.match(/^([a-z][a-z0-9+.-]*):/i);
  if (m && m[1].toLowerCase() !== 'https') {
    // "example.com:8080/x" has a colon but no scheme — a host:port is not a scheme.
    const rest = s.slice(m[0].length);
    if (!/^\d+(\/|$)/.test(rest)) fail('scheme');
  }
}

function parseHttps(raw) {
  assertNoBadScheme(raw);
  let s = raw.trim();
  if (!/^https:\/\//i.test(s)) s = `https://${s}`;
  if (s.length > MAX_URL) fail('long');
  let u;
  try { u = new URL(s); } catch (e) { fail('invalid'); }
  if (u.protocol !== 'https:') fail('scheme');
  if (u.username || u.password) fail('invalid');
  const host = u.hostname.toLowerCase();
  if (host === 'localhost' || IPV4.test(host) || host.includes(':') || !host.includes('.')) fail('invalid');
  return u;
}

const hostNoWww = (u) => u.hostname.toLowerCase().replace(/^www\./, '');
const short = (s) => (s.length > 60 ? `${s.slice(0, 59)}…` : s);

function handleFrom(raw, hosts, re, min, max) {
  let v = raw.trim().replace(/^@/, '');
  if (/[/.]/.test(v) && hosts.some((h) => v.toLowerCase().includes(h))) {
    const u = parseHttps(v);
    if (!hosts.includes(hostNoWww(u))) fail('invalid');
    const parts = u.pathname.split('/').filter(Boolean);
    v = parts[parts[0] === 'add' ? 1 : 0] || '';
    v = v.replace(/^@/, '');
  }
  if (v.length < min || v.length > max || !re.test(v)) fail('handle');
  return v;
}

/**
 * @returns {{url: string, display: string}} canonical https url + short readable text for the image.
 * @throws {QrTargetError} with a `code` (i18n key suffix).
 */
export function normalizeQrTarget(type, input) {
  const raw = String(input ?? '').trim();
  if (!raw) fail('empty');
  switch (type) {
    case 'instagram': {
      const h = handleFrom(raw, ['instagram.com', 'instagr.am'], /^[A-Za-z0-9._]+$/, 1, 30);
      return { url: `https://instagram.com/${h}`, display: `instagram.com/${h}` };
    }
    case 'snapchat': {
      const h = handleFrom(raw, ['snapchat.com'], /^[A-Za-z0-9._-]+$/, 3, 15);
      return { url: `https://www.snapchat.com/add/${h}`, display: `snapchat.com/add/${h}` };
    }
    case 'whatsapp': {
      if (/wa\.me|whatsapp\.com/i.test(raw)) {
        const u = parseHttps(raw);
        const digits = (u.pathname.replace(/\D/g, '') || '');
        if (hostNoWww(u) === 'wa.me' && digits.length >= 8 && digits.length <= 15) {
          return { url: `https://wa.me/${digits}`, display: `wa.me/${digits}` };
        }
        fail('whatsapp');
      }
      if (/[A-Za-z]/.test(raw)) fail('whatsapp');
      let digits = raw.replace(/[\s\-()+.]/g, '');
      if (!/^\d+$/.test(digits)) fail('whatsapp');
      digits = digits.replace(/^00/, '');
      if (digits.length < 8 || digits.length > 15) fail('country');
      return { url: `https://wa.me/${digits}`, display: `wa.me/${digits}` };
    }
    case 'google':
    case 'delivery':
    case 'link': {
      const u = parseHttps(raw);
      const display = short(`${hostNoWww(u)}${u.pathname === '/' ? '' : u.pathname}`.replace(/\/$/, ''));
      return { url: u.toString(), display };
    }
    default:
      return fail('type');
  }
}

/** Wi-Fi payload, encoded directly in the QR (no redirect, not counted). Special chars escaped per the WIFI: spec. */
export function wifiPayload(ssid, pass) {
  const esc = (s) => String(s).replace(/([\\;,:"])/g, '\\$1');
  if (!String(ssid || '').trim()) fail('ssid');
  return pass ? `WIFI:T:WPA;S:${esc(ssid)};P:${esc(pass)};;` : `WIFI:T:nopass;S:${esc(ssid)};;`;
}
